import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { contactInfo } from '@/mocks/en/contact';

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formSent, setFormSent] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSent(true);
    setTimeout(() => {
      setFormSent(false);
      setFormData({ name: '', email: '', message: '' });
    }, 3000);
  };

  const isVisible = (id: string) => visibleSections.has(id);

  return (
    <div className="bg-cream min-h-screen">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-20">
          <div className="max-w-4xl">
            <h1 className="font-serif text-5xl md:text-7xl text-forest leading-[0.95] mb-8">
              Start the<br />
              <span className="italic">conversation</span>
            </h1>
            <p className="font-grotesk text-lg text-forest/60 max-w-xl leading-relaxed">
              Whether you are ready to book or just exploring options, we are here to answer your questions. 
              Every pet is unique, and so is our approach.
            </p>
          </div>
        </section>

        {/* Contact Grid */}
        <section id="contact-grid" data-animate className={`px-6 md:px-12 lg:px-20 pb-24 md:pb-32 transition-all duration-700 ${isVisible('contact-grid') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">

            {/* Form — 3/5 width */}
            <div className="lg:col-span-3">
              <h2 className="font-serif text-4xl md:text-5xl text-forest mb-12">Begin the Conversation</h2>
              <form
                onSubmit={handleFormSubmit}
                data-readdy-form
                className="space-y-10"
                action="https://readdy.ai/api/form/d7mciutbmnmv4i2ujiug"
                method="POST"
              >
                <div>
                  <label
                    className="block font-grotesk uppercase text-sienna mb-4 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full h-12 bg-transparent border-b border-forest/15 px-0 font-grotesk text-sm text-forest placeholder:text-forest/25 outline-none focus:border-sienna transition-colors"
                    required
                  />
                </div>
                <div>
                  <label
                    className="block font-grotesk uppercase text-sienna mb-4 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="w-full h-12 bg-transparent border-b border-forest/15 px-0 font-grotesk text-sm text-forest placeholder:text-forest/25 outline-none focus:border-sienna transition-colors"
                    required
                  />
                </div>
                <div>
                  <label
                    className="block font-grotesk uppercase text-sienna mb-4 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your pet and what you need..."
                    rows={5}
                    maxLength={500}
                    className="w-full bg-transparent border-b border-forest/15 px-0 py-3 font-grotesk text-sm text-forest placeholder:text-forest/25 outline-none focus:border-sienna transition-colors resize-none"
                    required
                  />
                  <p className="font-grotesk text-micro text-forest/25 mt-2 text-right">{formData.message.length}/500</p>
                </div>
                <div>
                  <button
                    type="submit"
                    className="h-14 px-12 bg-forest text-cream font-grotesk text-sm tracking-widest uppercase rounded-none hover:bg-forest/90 transition-colors whitespace-nowrap"
                  >
                    {formSent ? 'Message Sent' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>

            {/* Info — 2/5 width */}
            <div className="lg:col-span-2 lg:pt-20">
              <div className="space-y-10">
                <div>
                  <p
                    className="font-grotesk uppercase text-sienna mb-3 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Visit Us
                  </p>
                  <p className="font-grotesk text-base text-forest leading-loose">{contactInfo.address}</p>
                </div>
                <div>
                  <p
                    className="font-grotesk uppercase text-sienna mb-3 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Email
                  </p>
                  <a
                    href="mailto://hodchenkovevgen@gmail.com"
                    className="font-grotesk text-base text-forest leading-loose hover:text-sienna transition-colors block"
                  >
                    hodchenkovevgen@gmail.com
                  </a>
                </div>
                <div>
                  <p
                    className="font-grotesk uppercase text-sienna mb-3 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Phone
                  </p>
                  <a
                    href="tel://+3 (067) 456 42 41"
                    className="font-grotesk text-base text-forest leading-loose hover:text-sienna transition-colors block"
                  >
                    +3 (067) 456 42 41
                  </a>
                </div>
                <div>
                  <p
                    className="font-grotesk uppercase text-sienna mb-3 font-bold"
                    style={{ letterSpacing: '0.2em', fontSize: '13px' }}
                  >
                    Hours
                  </p>
                  <p className="font-grotesk text-base text-forest leading-loose whitespace-pre-line">{contactInfo.hours}</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Map */}
        <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
          <div className="border border-forest/10 overflow-hidden">
            <iframe
              src="https://maps.google.com/maps?q=%D0%91%D1%96%D1%80%D0%B6%D0%BE%D0%B2%D0%B0%20%D0%BF%D0%BB%D0%BE%D1%89%D0%B0%2C%201%2C%20%D0%9E%D0%B4%D0%B5%D1%81%D0%B0%2C%20%D0%A3%D0%BA%D1%80%D0%B0%D1%97%D0%BD%D0%B0&t=&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Paw & Co. Location"
            />
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}